import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Globe, 
  ArrowLeft, 
  CheckCircle2, 
  Loader2,
  Copy,
  ExternalLink
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DomainConnect = () => {
  const navigate = useNavigate();
  const { purchaseId } = useParams();
  const { user } = useContext(AuthContext);
  const [domainData, setDomainData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    fetchDomainDetails();
  }, [user, purchaseId]);

  const fetchDomainDetails = async () => {
    try {
      const response = await axios.get(`${API}/domains/${purchaseId}`);
      setDomainData(response.data);
      
      if (response.data.status === "ready_to_claim" || response.data.status === "awaiting_acceptance") {
        navigate(`/domain-claim/${purchaseId}`);
      }
    } catch (error) {
      console.error("Error fetching domain:", error);
      toast.error("Failed to load domain details");
      navigate("/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Copied!");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleVerifyConnection = async () => {
    setIsVerifying(true);
    try {
      await axios.post(`${API}/domains/verify-connection`, {
        purchase_id: purchaseId
      });
      
      toast.success("Domain is live!");
      setDomainData(prev => ({
        ...prev,
        status: "live"
      }));
    } catch (error) {
      console.error("Verify error:", error);
      toast.info("Not connected yet. DNS changes can take a few hours.");
    } finally {
      setIsVerifying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const status = domainData?.status || "ready_to_connect";

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Navigation */}
      <nav className="nav-glass sticky top-0 z-50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
            <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Bloomwright Studio
            </span>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-[#78716C] hover:text-[#2C332E]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="max-w-lg mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-[#8DA399]/10 flex items-center justify-center mx-auto mb-4">
            <Globe className="w-7 h-7 text-[#8DA399]" />
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold text-[#2C332E] mb-2">
            Connect to Emergent
          </h1>
          <p className="text-[#78716C]">
            Your site goes live once DNS is in place.
          </p>
        </div>

        {/* Domain Display */}
        <div className="bg-[#C77D63]/10 rounded-xl p-4 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#78716C] mb-1">Your domain</p>
              <p className="text-lg font-semibold text-[#C77D63]">{domainData?.domain}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopy(domainData?.domain, 'domain')}
              className="text-[#78716C] hover:text-[#2C332E]"
            >
              {copiedField === 'domain' ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {status === "live" ? (
          /* Live State */
          <Card className="bg-green-50 rounded-xl border border-green-200">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h2 className="text-xl font-semibold text-[#2C332E] mb-2">
                Your site is live!
              </h2>
              <p className="text-sm text-[#78716C] mb-4">
                {domainData?.domain} is connected and ready.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => window.open(`https://${domainData?.domain}`, '_blank')}
                  className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full"
                >
                  Visit your site
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  variant="outline"
                  className="rounded-full"
                >
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Step-by-step Instructions */}
            <div className="space-y-6 mb-8">
              {/* Step 1 */}
              <div>
                <h3 className="font-semibold text-[#2C332E] mb-2">1. Add your domain in Emergent</h3>
                <p className="text-sm text-[#78716C]">
                  Open your project → Domains / Custom Domain<br />
                  Add: <span className="font-medium text-[#C77D63]">{domainData?.domain}</span>
                </p>
              </div>

              {/* Step 2 */}
              <div>
                <h3 className="font-semibold text-[#2C332E] mb-2">2. Copy the DNS records shown</h3>
                <p className="text-sm text-[#78716C]">
                  Emergent will show the exact records you need. Keep that screen open.
                </p>
              </div>

              {/* Step 3 */}
              <div>
                <h3 className="font-semibold text-[#2C332E] mb-2">3. Paste into Namecheap</h3>
                <p className="text-sm text-[#78716C]">
                  Namecheap → Domain List → Manage → Advanced DNS<br />
                  Add the records exactly as shown in Emergent. Save.
                </p>
              </div>
            </div>

            {/* Calm Note */}
            <div className="bg-[#F5F0EB] rounded-lg p-4 mb-8">
              <p className="text-xs text-[#78716C]">
                If you see default parking records for @ or www, replace them with the Emergent records.
              </p>
            </div>

            {/* Verify Section */}
            <Card className="bg-white rounded-xl border-[#E6E1DB]/50 shadow-soft mb-6">
              <CardContent className="p-5">
                <p className="text-sm text-[#78716C] mb-4">
                  Some domains connect in minutes. Others take a little longer to settle.
                </p>
                <Button
                  onClick={handleVerifyConnection}
                  disabled={isVerifying}
                  className="w-full bg-[#8DA399] hover:bg-[#7A8F86] text-white rounded-full"
                  data-testid="verify-connection-btn"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    "Verify connection"
                  )}
                </Button>
              </CardContent>
            </Card>

            <div className="text-center">
              <button
                onClick={() => navigate("/dashboard")}
                className="text-sm text-[#78716C] hover:text-[#2C332E] underline"
              >
                Try again later
              </button>
            </div>

            {/* Help */}
            <div className="mt-8 text-center">
              <p className="text-xs text-[#78716C] mb-2">Need help?</p>
              <p className="text-xs text-[#78716C]">
                Send a screenshot of your Emergent DNS screen and your Namecheap Advanced DNS screen. We'll point out exactly what to add.
              </p>
              <Button
                variant="link"
                size="sm"
                className="text-[#C77D63] hover:text-[#B06A52] text-xs mt-2"
                onClick={() => window.open('mailto:support@bloomwrightstudio.com?subject=Help connecting domain: ' + domainData?.domain, '_blank')}
              >
                Get help
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default DomainConnect;
